import { useEffect, useState, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './DailyLogPage.css';
import '../pages/Landing.css';

/* ============================================================================
   Types
   ============================================================================ */

interface LogEntry {
  id: string;
  content: string;
  metadata: Record<string, unknown>;
  occurred_at: string | null;
  created_at: string;
}

interface NutritionEntries {
  breakfast: LogEntry[];
  lunch: LogEntry[];
  dinner: LogEntry[];
  snack: LogEntry[];
}

interface LogData {
  nanoid: string;
  log_date: string;
  first_name: string | null;
  entries: {
    nutrition: NutritionEntries;
    exercise: LogEntry[];
    mind: LogEntry[];
  };
}

type PageState = 'loading' | 'loaded' | 'not_found' | 'expired' | 'error';

/* ============================================================================
   Helpers
   ============================================================================ */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

function formatLogDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function timeAgo(isoStr: string | null): string {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return '';
}

/* ============================================================================
   Shell
   ============================================================================ */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="daily-log-page">
      <PublicHeader />
      {children}
      <footer className="daily-log-footer">
        <div className="daily-log-container daily-log-footer-inner">
          <div className="daily-log-footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="daily-log-footer-logo" />
            <span>mosaime</span>
          </div>
          <p className="daily-log-footer-copy">
            &copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.
          </p>
          <div style={{ display: 'flex', gap: '16px', fontSize: '0.8rem' }}>
            <Link to="/how-it-works" style={{ color: '#888', textDecoration: 'none' }}>How It Works</Link>
            <Link to="/login" style={{ color: '#888', textDecoration: 'none' }}>Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============================================================================
   EntryItem — read-only display of a single entry
   ============================================================================ */

function EntryItem({ entry }: { entry: LogEntry }) {
  return (
    <li className="dl-entry">
      <span className="dl-entry-dot" />
      <div className="dl-entry-text">
        {entry.content}
        {timeAgo(entry.occurred_at || entry.created_at) && (
          <div className="dl-entry-meta">{timeAgo(entry.occurred_at || entry.created_at)}</div>
        )}
      </div>
    </li>
  );
}

/* ============================================================================
   EntryGroup — a labeled, read-only list of entries
   ============================================================================ */

function EntryGroup({ label, entries }: { label: string; entries: LogEntry[] }) {
  return (
    <div className="dl-subsection">
      <div className="dl-subsection-head">
        <p className="dl-subsection-label">{label}</p>
        {entries.length > 0 && (
          <span className="dl-subsection-count">{entries.length} logged</span>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="dl-empty">Nothing logged yet</p>
      ) : (
        <ul className="dl-entry-list">
          {entries.map(e => (
            <EntryItem key={e.id} entry={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================================
   Page
   ============================================================================ */

export function DailyLogPage() {
  const { nanoid } = useParams<{ nanoid: string }>();
  const [logData, setLogData] = useState<LogData | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => { window.scrollTo(0, 0); }, [nanoid]);

  useEffect(() => {
    if (!nanoid) { setPageState('not_found'); return; }

    fetch(`${API_BASE}/daily-log/${nanoid}`)
      .then(res => {
        if (res.status === 404) throw Object.assign(new Error(), { code: 'not_found' });
        if (res.status === 410) throw Object.assign(new Error(), { code: 'expired' });
        if (!res.ok)           throw Object.assign(new Error(), { code: 'error' });
        return res.json();
      })
      .then(json => {
        if (!json.success) throw Object.assign(new Error(), { code: 'error' });
        setLogData(json.data as LogData);
        setPageState('loaded');
      })
      .catch((err: unknown) => {
        const code = (err as { code?: PageState })?.code;
        setPageState(code ?? 'error');
      });
  }, [nanoid]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <Shell>
        <div className="dl-state-wrap">
          <div className="dl-state-icon">...</div>
          <p className="dl-state-title">Loading your log...</p>
        </div>
      </Shell>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (pageState === 'not_found') {
    return (
      <Shell>
        <div className="dl-state-wrap">
          <div className="dl-state-icon">404</div>
          <p className="dl-state-title">Log not found</p>
          <p className="dl-state-body">This link doesn't match any daily log. Double-check the URL or ask Mosaime to send you a new one.</p>
        </div>
      </Shell>
    );
  }

  // ── Expired ──────────────────────────────────────────────────────────────
  if (pageState === 'expired') {
    return (
      <Shell>
        <div className="dl-state-wrap">
          <div className="dl-state-icon">&#x231B;</div>
          <p className="dl-state-title">This link has expired</p>
          <p className="dl-state-body">Daily log links are active for 24 hours. Text Mosaime and a new one will arrive at 7:30 PM tonight.</p>
        </div>
      </Shell>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (pageState === 'error' || !logData) {
    return (
      <Shell>
        <div className="dl-state-wrap">
          <div className="dl-state-icon">!</div>
          <p className="dl-state-title">Something went wrong</p>
          <p className="dl-state-body">We couldn't load your daily log. Try refreshing the page or texting Mosaime directly.</p>
        </div>
      </Shell>
    );
  }

  // ── Loaded ───────────────────────────────────────────────────────────────
  const { log_date, entries } = logData;
  const hour = new Date().getHours();
  const timeGreet = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <Shell>
      {/* ── Hero ── */}
      <section className="daily-log-hero">
        <div className="daily-log-container daily-log-hero-inner">
          <p className="daily-log-label">Daily Wellness Log</p>
          <h1 className="daily-log-title">{formatLogDate(log_date)}</h1>
          <p className="daily-log-greeting">{timeGreet}! Here's everything you've logged today.</p>
        </div>
      </section>

      {/* ── Body ── */}
      <main className="daily-log-body">
        <div className="daily-log-container">

          {/* ── How to make changes ── */}
          <div className="dl-instructions">
            <p className="dl-instructions-title">Need to make a change?</p>
            <p className="dl-instructions-body">
              Just text Mosaime — it updates your log for you. For example:
            </p>
            <ul className="dl-instructions-examples">
              <li>&ldquo;add a banana to breakfast&rdquo;</li>
              <li>&ldquo;change lunch to a chicken salad&rdquo;</li>
              <li>&ldquo;delete my afternoon snack&rdquo;</li>
            </ul>
          </div>

          {/* ── Nutrition ── */}
          <section className="dl-section">
            <div className="dl-section-header">
              <span className="dl-section-icon" aria-hidden>&#x1F955;</span>
              <h2 className="dl-section-title">Nutrition</h2>
            </div>
            <EntryGroup label="Breakfast" entries={entries.nutrition.breakfast} />
            <EntryGroup label="Lunch" entries={entries.nutrition.lunch} />
            <EntryGroup label="Dinner" entries={entries.nutrition.dinner} />
            <EntryGroup label="Snacks & Other" entries={entries.nutrition.snack} />
          </section>

          {/* ── Movement ── */}
          <section className="dl-section">
            <div className="dl-section-header">
              <span className="dl-section-icon" aria-hidden>&#x1F3C3;</span>
              <h2 className="dl-section-title">Movement</h2>
            </div>
            <EntryGroup label="Activity" entries={entries.exercise} />
          </section>

          {/* ── Mind ── */}
          <section className="dl-section">
            <div className="dl-section-header">
              <span className="dl-section-icon" aria-hidden>&#x1F9E0;</span>
              <h2 className="dl-section-title">Mind</h2>
            </div>
            <EntryGroup label="Mind & Sleep" entries={entries.mind} />
          </section>

        </div>
      </main>
    </Shell>
  );
}

export default DailyLogPage;
